import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ListChecks, Users, KanbanSquare, Target,
  ChevronDown, Activity, Building2, FolderOpen, FileSignature,
  BarChart3, FileText, Search, ReceiptText, Package, TrendingUp,
} from "lucide-react";
import NotificationBell from "@/components/app/NotificationBell";
import AccountMenu from "@/components/app/AccountMenu";
import CommandPalette from "@/components/app/CommandPalette";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const PIPELINE_ITEMS = [
  { to: "/prospects",  label: "Prospek",   icon: ListChecks,   testid: "nav-prospects" },
  { to: "/kanban",     label: "Kanban",    icon: KanbanSquare, testid: "nav-kanban" },
  { to: "/aktivitas",  label: "Aktivitas", icon: Activity,     testid: "nav-activities" },
];

const REPORTS_ITEMS_OWNER = [
  { to: "/reports/sales", label: "Laporan Penjualan", icon: BarChart3, testid: "nav-report-sales" },
  { to: "/reports/team",  label: "Laporan Tim",       icon: FileText,  testid: "nav-report-team" },
];
const REPORTS_ITEMS_SELF = [
  { to: "/reports/sales", label: "Laporan Penjualan", icon: BarChart3, testid: "nav-report-sales" },
  { to: "/reports/team",  label: "Rapor Saya",        icon: FileText,  testid: "nav-report-team" },
];
const LABA_RUGI_ITEM = { to: "/reports/laba-rugi", label: "Laporan Laba Rugi", icon: TrendingUp, testid: "nav-report-pl" };

const DOCS_ITEMS = [
  { section: "Penjualan" },
  { to: "/quotations",      label: "Quotation",      icon: FileSignature, testid: "nav-quotations" },
  { to: "/invoices",        label: "Invoice",        icon: ReceiptText,   testid: "nav-invoices" },
  { section: "Pembelian" },
  { to: "/purchase-orders", label: "Purchase Order", icon: Package,       testid: "nav-po" },
];

const MANAGEMENT_ITEMS = [
  { to: "/team",    label: "Tim",    icon: Users,  testid: "nav-team" },
  { to: "/targets", label: "Target", icon: Target, testid: "nav-targets" },
];

export default function Layout() {
  const { user } = useAuth();
  const location = useLocation();

  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isPrivileged = user?.role === "owner" || user?.role === "sales_director";
  const isFinance = user?.role === "finance";
  const canSeePL = user?.role === "owner" || user?.role === "finance" || user?.role === "sales_director";
  const baseReports = user?.role === "owner" ? REPORTS_ITEMS_OWNER : (isFinance ? [] : REPORTS_ITEMS_SELF);
  const reportsItems = canSeePL ? [...baseReports, LABA_RUGI_ITEM] : baseReports;

  const pipelineActive = PIPELINE_ITEMS.some((i) => location.pathname.startsWith(i.to));
  const reportsActive = location.pathname.startsWith("/reports");
  const managementActive = MANAGEMENT_ITEMS.some((i) => location.pathname.startsWith(i.to));
  const docsActive = DOCS_ITEMS.some((i) => i.to && location.pathname.startsWith(i.to));

  return (
    <TooltipProvider delayDuration={100}>
      <div className="min-h-screen flex">
        {/* Left rail — transparent narrow, nav icons (menu + submenu, icon-only) */}
        <aside className="shrink-0 pl-4 pr-2 py-6 w-[72px] flex flex-col items-center gap-2">
          <RailNav to="/dashboard" label="Beranda" icon={LayoutDashboard} testid="rail-dashboard" />

          <div className="w-6 h-px bg-black/10 my-1" />

          {/* Pipeline submenu icons — hidden for finance (sales-only flow) */}
          {!isFinance && (
            <>
              <RailNav to="/prospects"  label="Prospek"    icon={ListChecks}   testid="rail-prospects" />
              <RailNav to="/kanban"     label="Kanban"     icon={KanbanSquare} testid="rail-kanban" />
              <RailNav to="/aktivitas"  label="Aktivitas"  icon={Activity}     testid="rail-activities" />

              <div className="w-6 h-px bg-black/10 my-1" />
            </>
          )}

          <RailNav to="/projects" label="Proyek" icon={FolderOpen} testid="rail-projects" />
          <RailNav to="/quotations" label="Quotation" icon={FileSignature} testid="rail-quotations" />
          <RailNav to="/invoices" label="Invoice" icon={ReceiptText} testid="rail-invoices" />
          <RailNav to="/purchase-orders" label="Purchase Order" icon={Package} testid="rail-po" />
          <RailNav to="/clients" label="Mitra" icon={Building2} testid="rail-clients" />

          {reportsItems.length > 0 && (
            <>
              <div className="w-6 h-px bg-black/10 my-1" />

              {/* Reporting submenu icons */}
              {reportsItems.map((r) => (
                <RailNav key={r.to} to={r.to} label={r.label} icon={r.icon} testid={`rail-${r.testid}`} />
              ))}
            </>
          )}

          {isPrivileged && (
            <>
              <div className="w-6 h-px bg-black/10 my-1" />
              {MANAGEMENT_ITEMS.map((m) => (
                <RailNav key={m.to} to={m.to} label={m.label} icon={m.icon} testid={`rail-${m.testid}`} />
              ))}
            </>
          )}

          <div className="flex-1" />

          {/* Version pip at bottom */}
          <div className="text-[9px] text-black/40 font-medium tracking-widest">v1.0</div>
        </aside>

        <main className="flex-1 min-w-0 overflow-x-auto">
          <div className="max-w-[1500px] mx-auto p-6">
            {/* Top bar with nav — transparent, buttons merge with bg */}
            <header
              className="h-16 flex items-center justify-between mb-6 gap-4"
              data-testid="topbar"
            >
              {/* Brand logo */}
              <NavLink
                to="/dashboard"
                className="flex items-center min-w-0 shrink-0"
                data-testid="topbar-brand"
              >
                <img
                  src="/supersales-logo.png"
                  alt="Super Sales"
                  className="h-7 w-auto select-none"
                  draggable={false}
                />
              </NavLink>

              {/* Center: pill nav */}
              <nav className="flex items-center gap-0.5" data-testid="topbar-nav">
                <TopNavItem to="/dashboard" label="Beranda" testid="nav-dashboard" />
                {!isFinance && (
                  <TopNavDropdown
                    label="Pipeline"
                    active={pipelineActive}
                    items={PIPELINE_ITEMS}
                    testid="nav-pipeline"
                  />
                )}
                <TopNavItem to="/projects" label="Proyek" testid="nav-projects" />
                <TopNavDropdown
                  label="Dokumen"
                  active={docsActive}
                  items={DOCS_ITEMS}
                  testid="nav-docs"
                />
                <TopNavItem to="/clients" label="Mitra" testid="nav-clients" />
                {reportsItems.length > 0 && (
                  <TopNavDropdown
                    label="Laporan"
                    active={reportsActive}
                    items={reportsItems}
                    testid="nav-reports"
                  />
                )}
                {isPrivileged && (
                  <TopNavDropdown
                    label="Manajemen"
                    active={managementActive}
                    items={MANAGEMENT_ITEMS}
                    testid="nav-management"
                  />
                )}
              </nav>

              {/* Right actions */}
              <div className="flex items-center gap-1.5 shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setPaletteOpen(true)}
                      data-testid="topbar-search-btn"
                      aria-label="Buka pencarian global"
                      className="w-11 h-11 rounded-full bg-white text-[#14161F] flex items-center justify-center transition-all shadow-[0_2px_8px_rgba(20,22,31,0.06)] hover:shadow-[0_4px_14px_rgba(20,22,31,0.10)]"
                    >
                      <Search className="w-[18px] h-[18px]" strokeWidth={1.5} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Cari (Ctrl+K)</TooltipContent>
                </Tooltip>
                <NotificationBell />
                <AccountMenu />
              </div>
            </header>
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </TooltipProvider>
  );
}

function RailNav({ to, icon: Icon, label, testid }) {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(to + "/");
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={to}
          data-testid={testid}
          className={
            active
              ? "w-11 h-11 rounded-full flex items-center justify-center transition-all bg-[#14161F] text-white shadow-[0_6px_16px_rgba(20,22,31,0.30)]"
              : "w-11 h-11 rounded-full flex items-center justify-center transition-all bg-white text-[#14161F] shadow-[0_2px_8px_rgba(20,22,31,0.06)] hover:shadow-[0_4px_14px_rgba(20,22,31,0.10)]"
          }
        >
          <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right" className="text-xs font-medium">{label}</TooltipContent>
    </Tooltip>
  );
}

function TopNavItem({ to, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `h-11 px-4 rounded-full text-sm font-semibold flex items-center transition-colors whitespace-nowrap ${
          isActive
            ? "bg-[#14161F] text-white shadow-[0_6px_16px_rgba(20,22,31,0.28)]"
            : "text-[#626061] hover:text-[#14161F] hover:bg-white/60"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function TopNavDropdown({ label, items, active, testid }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testid}
          className={`h-11 px-4 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors whitespace-nowrap ${
            active
              ? "bg-[#14161F] text-white shadow-[0_6px_16px_rgba(20,22,31,0.28)]"
              : "text-[#626061] hover:text-[#14161F] hover:bg-white/60"
          }`}
        >
          {label}
          <ChevronDown className="w-3.5 h-3.5 opacity-70" strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="min-w-[210px]">
        {items.map(({ to, label: l, icon: I, testid: t, section }, i) => (
          section ? (
            <div
              key={`s-${section}`}
              className={`px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#A0A3A8] ${i === 0 ? "pt-1" : "pt-2.5 mt-1 border-t border-[#F1F2F5]"}`}
            >
              {section}
            </div>
          ) : (
            <DropdownMenuItem
              key={to}
              data-testid={t}
              onSelect={() => navigate(to)}
              className="gap-2.5 cursor-pointer"
            >
              <I className="w-4 h-4 text-[#626061]" strokeWidth={1.5} />
              <span className="text-sm font-medium">{l}</span>
            </DropdownMenuItem>
          )
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
